
import { Component, Vue, Watch } from "vue-property-decorator";
import router from "@/router";
import Loading from "@/components/Loading.vue";
import axios, { AxiosResponse } from "axios";
import { apiURL, environment } from "@/App.vue";
import NavBar from "@/components/NavBar.vue";
import ProfileBuild from "@/components/Signup/ProfileBuild.vue";
import InvalidVerify from "@/components/Signup/InvalidVerify.vue";
import {
  SignupData,
  Country,
  getSignupFlow,
  SignupFlow,
  IndustryItem,
} from "../Models/SignupData";
import { CreationResponse } from "../Models/CreationResponse";
import { ProfileResponse } from "../Models/ProfileResponse";
import ResponseModal from "@/components/Initial/ResponseModal.vue";
import Banner from "@/components/Banner.vue";
import { Countries } from "@/countries";
import { UTMHelper, ReferralRock, Branding, Industries } from "@/helper";
import SplitContent from "@/components/SplitContent.vue";

@Component({
  components: {
    NavBar,
    Loading,
    InvalidVerify,
    ProfileBuild,
    ResponseModal,
    Banner,
    SplitContent,
  },
})
export default class SignUp extends Vue {
  public formLoading: boolean = true;
  public verificationChecked: boolean = false;
  public verificationValid: boolean | null = null;
  public signupData: SignupData | null = null;
  public password: string = "";
  public confirmPassword: string = "";
  public profileResponse: ProfileResponse | null = null;
  public hideDivisionFields: boolean = false;
  public hideContactFields: boolean = false;
  public profileCreateResponse: CreationResponse | null = null;
  public countriesList: Country[] = Countries;
  public mainIndustry: IndustryItem | null = null;

  public async created() {
    this.signupData = {
      ContactId: null,
      Title: null,
      Forename: null,
      Surname: null,
      Email: null,
      CompName: null,
      PhoneNumber: null,
      Password: null,
      Type: null,
      VerifyCode: null,
      Location: {
        CountryRegion: {
          IsoCode: null,
        },
      },
      UTMDictionary: {},
      FreeTrialCode: null,
      Branding: null,
      Industry: null,
    };

    let verifyCode: string | undefined;

    if (this.$route.params.token == null) {
      verifyCode = this.$route.query.verifycode as string;
    } else {
      verifyCode = this.$route.params.token as string;
    }

    if (verifyCode == null) {
      this.$router.push({
        name: "verifyinvalid",
      });
    }

    axios
      .get(apiURL() + "/api/Signup/VerifyContact", {
        params: {
          verifyCode: verifyCode,
        },
      })
      .then((response) => {
        this.handleVerifyResponse(response.data);
      })
      .catch((error) => {
        this.profileCreateResponse = {
          Type: "GeneralError",
        } as CreationResponse;
        this.formLoading = false;
      });
  }

  public handleVerifyResponse(response: SignupData): void {
    switch (response.Type) {
      case "VerifyCodeValidPasswordOnly":
        if (response.Surname !== "?" && response.Surname !== null) {
          this.hideContactFields = true;
        }
      case "VerifyCodeValidContactOnly":
        if (response.CompName !== "?" && response.CompName !== null) {
          this.hideDivisionFields = true;
        }
      case "VerifyCodeValid":
        this.signupData = response;
        if (this.signupData.Surname === "?") {
          this.signupData.Surname = null;
        }
        if (this.signupData.CompName === "?") {
          this.signupData.CompName = null;
        }
        this.formLoading = false;
        this.verificationValid = true;
        if (UTMHelper.hasUTMParams()) {
          this.signupData.UTMDictionary = UTMHelper.getUTMParams();
        }
        break;
      default:
        this.formLoading = false;
    }
    this.verificationChecked = true;
  }

  public validateForm(): boolean {
    const form = document.querySelector(".needs-validation") as HTMLFormElement;
    form.classList.add("was-validated");
    const passwordValid =
      !this.showPasswordInput ||
      (this.passwordValid && this.passwordStrengthValid);
    return form.checkValidity() && passwordValid;
  }

  public submitSignup(): void {
    if (this.validateForm()) {
      this.formLoading = true;
      this.signupData!.Password = this.password;
      this.signupData!.Branding = this.branding;

      axios
        .post(apiURL() + `/api/Signup/SignupContact`, this.signupData)
        .then((response) => {
          this.formLoading = false;
          this.handleSignupVerify(response.data);
        })
        .catch(() => {
          this.profileCreateResponse = {
            Type: "ProfileCreationError",
          } as CreationResponse;
          this.formLoading = false;
        });
    }
  }

  public handleSignupVerify(response: CreationResponse): void {
    if (
      response.Type === "SignupCreated" ||
      response.Type === "AccessTrialProvisioningStarted"
    ) {
      this.profileResponse = response;
      ReferralRock.updateReferral(
        this.signupData as {},
        response.LeadId,
        environment(),
      );
      router.push({
        name: "building",
        params: {
          profileResponse: JSON.stringify(this.profileResponse),
        },
      });
    } else if (response.Type === "RequestAdminAccess") {
      this.profileResponse = response;
      this.profileCreateResponse = {
        Type: response.Type,
      } as CreationResponse;
      this.formLoading = false;
    }
  }

  get showPasswordInput() {
    return !this.isAccessSignup;
  }

  get isAccessSignup() {
    if (!this.signupData || !this.signupData.VerifyCode) {
      return false;
    }

    return (
      getSignupFlow(this.signupData.VerifyCode) === SignupFlow.AccessIdentity
    );
  }

  public get isSplitContent() {
    return this.isAccessSignup;
  }

  public get branding() {
    return Branding.getBranding();
  }

  public get splashImage() {
    return Branding.getCurrentBrand().secondSplash;
  }

  public get passwordValid(): boolean {
    if (
      this.password.length > 0 &&
      this.confirmPassword.length > 0 &&
      this.password === this.confirmPassword
    ) {
      return true;
    } else {
      return false;
    }
  }

  public get passwordStrengthValid(): boolean {
    return /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{8,}$/.test(this.password);
  }

  public get sortedCountries() {
    return this.countriesList.sort((a, b) => {
      if (a.CountryName.toLowerCase() > b.CountryName.toLowerCase()) {
        return 1;
      }
      if (a.CountryName.toLowerCase() < b.CountryName.toLowerCase()) {
        return -1;
      }
      return 0;
    });
  }

  public get sortedMainIndustries() {
    return Industries;
  }

  public get sortedSubIndustries() {
    if (
      this.mainIndustry &&
      this.mainIndustry.SubIndustries &&
      this.hasSubIndustries
    ) {
      return this.mainIndustry.SubIndustries.sort((a, b) => {
        if (a.StandardIndustryCode === "333fb6") return 1;
        return a.Name.toLocaleLowerCase().localeCompare(
          b.Name.toLocaleLowerCase(),
        );
      });
    }
    return [];
  }

  public get hasSubIndustries() {
    return this.mainIndustry && this.mainIndustry.SubIndustries
      ? this.mainIndustry.SubIndustries.length > 0
      : false;
  }

  @Watch("mainIndustry")
  public onMainIndustryChange() {
    if (this.signupData) {
      if (
        this.mainIndustry &&
        (this.mainIndustry.Name === "DigitalProducts" ||
          this.mainIndustry.Name === "3rdPartyLogistics")
      ) {
        this.signupData.Industry = this.mainIndustry;
      } else {
        this.signupData.Industry = null;
      }
    }
  }
}
